/*
 * afEditor: autoFlow's own text editor, on the browser's contenteditable, the
 * way Gmail does it. The HTML it is given is kept as it is and edited in place;
 * nothing is rewritten into a model of its own. The formatting bar is the Blade
 * component <x-af.editor-toolbar set="compose|footer">, inside the same x-data.
 *
 *   x-data="afEditor({ wire, textarea, pictures, autofocus })"
 *
 *   wire       the Livewire property the HTML goes to (deferred), 300 ms after a
 *              change, on blur and on sync()
 *   textarea   true when a hidden <textarea x-ref="source"> holds the HTML for a
 *              plain form: read on load, kept equal on every change and on submit
 *   pictures   { property, url, maxBytes }: the Livewire upload property a
 *              picture goes up to, and the method that answers its address
 *   autofocus  put the caret in the text once it is up
 *
 * Refs: editable (the contenteditable), source, linkPopover, linkInput,
 * picturePicker. API: getHtml(), setHtml(html), focus(), insertHtml(html),
 * sync(); the element carrying x-data holds it as `afEditor`. Every change
 * fires `input` on the editable; a picture upload fires `af-editor-uploading`
 * (+1 / -1) and a refused one `af-editor-refused` (the reason).
 */
(function () {
  'use strict';

  var PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
  var PICKED_CLASS = 'af-editor-picked';

  function escapeHtml(text) {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Plain text as the editor writes it: the first line where the caret is, each next line a line block.
  function linesAsHtml(text) {
    return text.replace(/\r\n?/g, '\n').split('\n').map(function (line, index) {
      var html = escapeHtml(line.replace(/\t/g, '    ')).replace(/ {2}/g, '  ');

      if (index === 0) { return html; }

      return '<div>' + (html === '' ? '<br>' : html) + '</div>';
    }).join('');
  }

  // The HTML as given, without what would run on its own once it is in the page.
  function inert(html) {
    var template = document.createElement('template');
    template.innerHTML = html;
    Array.prototype.slice.call(template.content.querySelectorAll('*')).forEach(function (element) {
      if (element.tagName === 'SCRIPT') { element.parentNode.removeChild(element); return; }
      Array.prototype.slice.call(element.attributes).forEach(function (attribute) {
        var name = attribute.name.toLowerCase();
        if (name.indexOf('on') === 0 || ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attribute.value))) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return template.content;
  }

  function isPicture(file) {
    return PICTURE_TYPES.indexOf(file.type) !== -1;
  }

  function closestIn(node, tagName, root) {
    var current = node && node.nodeType === 3 ? node.parentNode : node;
    while (current && current !== root) {
      if (current.nodeType === 1 && current.tagName === tagName) { return current; }
      current = current.parentNode;
    }

    return null;
  }

  function rangeIn(root) {
    var selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) { return null; }
    var range = selection.getRangeAt(0);

    return root.contains(range.commonAncestorContainer) ? range : null;
  }

  function select(range) {
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function caretRangeAt(x, y) {
    if (document.caretRangeFromPoint) { return document.caretRangeFromPoint(x, y); }
    if (document.caretPositionFromPoint) {
      var position = document.caretPositionFromPoint(x, y);
      if (!position) { return null; }
      var range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);

      return range;
    }

    return null;
  }

  // The URL a link gets: as typed when it names its scheme, mailto: for an address, else https://.
  function linkAddress(typed) {
    var url = (typed || '').trim();
    if (url === '' || /^javascript:/i.test(url)) { return null; }
    if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.charAt(0) === '/' || url.charAt(0) === '#') { return url; }
    if (/^[^\s@/]+@[^\s@/]+$/.test(url)) { return 'mailto:' + url; }

    return 'https://' + url;
  }

  function afEditor(config) {
    config = config || {};

    var syncTimer = null;
    var savedRange = null;
    var linkRange = null;
    var picked = null;
    var pickedClass = null;
    var listeners = [];

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push([target, type, handler]);
    }

    function pick(image) {
      unpick();
      picked = image;
      pickedClass = image.getAttribute('class');
      image.classList.add(PICKED_CLASS);
    }

    function unpick() {
      if (!picked) { return; }
      if (pickedClass === null) { picked.removeAttribute('class'); } else { picked.setAttribute('class', pickedClass); }
      picked = null;
      pickedClass = null;
    }

    return {
      state: { bold: false, italic: false, insertUnorderedList: false, insertOrderedList: false, link: false, quote: false },
      block: 'div',
      linkOpen: false,
      linkUrl: '',
      linkEditing: false,

      init() {
        var self = this;
        var editable = this.$refs.editable;
        if (!editable) { return; }
        this.$el.afEditor = this;

        try {
          document.execCommand('defaultParagraphSeparator', false, 'div');
          document.execCommand('styleWithCSS', false, false);
        } catch (error) {}

        var initial = '';
        if (config.textarea && this.$refs.source) {
          initial = this.$refs.source.value;
        } else if (config.wire) {
          initial = this.$wire[config.wire] || '';
        }
        this.load(initial);

        listen(editable, 'input', function () { self.changed(); });
        listen(editable, 'blur', function () { self.sync(); });
        listen(editable, 'keydown', function (event) { self.keydown(event); });
        listen(editable, 'paste', function (event) { self.paste(event); });
        listen(editable, 'drop', function (event) { self.drop(event); });
        listen(editable, 'click', function (event) {
          if (event.target.tagName === 'IMG') {
            var range = document.createRange();
            range.selectNode(event.target);
            select(range);
            pick(event.target);
          }
        });
        listen(document, 'selectionchange', function () { self.selectionChanged(); });

        var form = config.textarea && this.$refs.source ? this.$refs.source.form : null;
        if (form) { listen(form, 'submit', function () { self.sync(); }); }

        if (config.autofocus) { this.$nextTick(function () { self.focus(); }); }
      },

      destroy() {
        clearTimeout(syncTimer);
        listeners.forEach(function (listener) { listener[0].removeEventListener(listener[1], listener[2]); });
        listeners = [];
        if (this.$el) { this.$el.afEditor = null; }
      },

      getHtml() {
        var editable = this.$refs.editable;
        if (!editable) { return ''; }
        if (editable.textContent.trim() === '' && !editable.querySelector('img, hr, table')) { return ''; }

        var image = picked;
        unpick();
        var html = editable.innerHTML;
        if (image && editable.contains(image)) { pick(image); }

        return html;
      },

      setHtml(html) {
        this.load(html);
        this.notify();
      },

      load(html) {
        var editable = this.$refs.editable;
        unpick();
        savedRange = null;
        editable.innerHTML = '';
        if (html && html.trim() !== '') {
          editable.appendChild(inert(html));
        } else {
          editable.innerHTML = '<div><br></div>';
        }
        if (config.textarea && this.$refs.source) { this.$refs.source.value = this.getHtml(); }
      },

      focus() {
        var editable = this.$refs.editable;
        if (!editable) { return; }
        editable.focus();
        if (savedRange && editable.contains(savedRange.startContainer)) { select(savedRange); return; }

        var node = editable;
        while (node.firstChild && node.firstChild.nodeType === 1 && ['BR', 'IMG', 'HR'].indexOf(node.firstChild.tagName) === -1) {
          node = node.firstChild;
        }
        var range = document.createRange();
        range.setStart(node, 0);
        range.collapse(true);
        select(range);
      },

      insertHtml(html) {
        this.restore();
        document.execCommand('insertHTML', false, html);
        this.changed();
      },

      // A change made other than by typing or a browser command: tell whoever listens, as typing does.
      notify() {
        if (this.$refs.editable) { this.$refs.editable.dispatchEvent(new Event('input', { bubbles: true })); }
      },

      changed() {
        var self = this;
        if (config.textarea && this.$refs.source) { this.$refs.source.value = this.getHtml(); }
        if (config.wire) {
          clearTimeout(syncTimer);
          syncTimer = setTimeout(function () { self.sync(); }, 300);
        }
        this.selectionChanged();
      },

      sync() {
        clearTimeout(syncTimer);
        if (!this.$refs.editable) { return; }
        if (config.textarea && this.$refs.source) { this.$refs.source.value = this.getHtml(); }
        if (config.wire) { this.$wire.set(config.wire, this.getHtml(), false); }
      },

      // Focus back on the text with the selection it had before a button or field took it.
      restore() {
        var editable = this.$refs.editable;
        if (document.activeElement !== editable) { editable.focus({ preventScroll: true }); }
        if (savedRange && editable.contains(savedRange.startContainer)) { select(savedRange); }

        return rangeIn(editable);
      },

      selectionChanged() {
        var editable = this.$refs.editable;
        var range = editable ? rangeIn(editable) : null;
        if (!range) { return; }
        savedRange = range.cloneRange();

        var only = range.startContainer === range.endContainer && range.endOffset - range.startOffset === 1
          ? range.startContainer.childNodes[range.startOffset]
          : null;
        if (picked && only !== picked) { unpick(); }

        var self = this;
        ['bold', 'italic', 'insertUnorderedList', 'insertOrderedList'].forEach(function (command) {
          var on = false;
          try { on = document.queryCommandState(command); } catch (error) {}
          self.state[command] = on;
        });
        this.state.link = closestIn(range.startContainer, 'A', editable) !== null;
        this.state.quote = closestIn(range.startContainer, 'BLOCKQUOTE', editable) !== null;
        this.block = closestIn(range.startContainer, 'H2', editable) ? 'h2' : (closestIn(range.startContainer, 'H3', editable) ? 'h3' : 'div');
      },

      run(command) {
        if (command === 'link') { this.openLink(); return; }
        if (command === 'quote') { this.toggleQuote(); return; }
        if (command === 'picture') {
          if (this.$refs.picturePicker) { this.$refs.picturePicker.click(); }
          return;
        }
        this.restore();
        document.execCommand(command, false, null);
        this.changed();
      },

      formatBlock(tag) {
        this.restore();
        document.execCommand('formatBlock', false, '<' + tag + '>');
        this.changed();
      },

      keydown(event) {
        var key = (event.key || '').toLowerCase();
        if (!(event.metaKey || event.ctrlKey) || event.altKey) { return; }

        var command = null;
        if (key === 'b' && !event.shiftKey) { command = 'bold'; }
        if (key === 'i' && !event.shiftKey) { command = 'italic'; }
        if (key === 'k' && !event.shiftKey) { command = 'link'; }
        if (key === 'z') { command = event.shiftKey ? 'redo' : 'undo'; }
        if (key === 'y' && event.ctrlKey && !event.metaKey) { command = 'redo'; }
        if (command === null) { return; }

        event.preventDefault();
        this.run(command);
      },

      // The selection's blocks go into a quote; inside one, they come out of it.
      toggleQuote() {
        var editable = this.$refs.editable;
        var range = this.restore();
        if (!range) { return; }

        var bounds = [range.startContainer, range.startOffset, range.endContainer, range.endOffset];
        var quote = closestIn(range.commonAncestorContainer, 'BLOCKQUOTE', editable) || closestIn(range.startContainer, 'BLOCKQUOTE', editable);
        var last;

        if (quote) {
          var inside = Array.prototype.filter.call(quote.childNodes, function (node) { return range.intersectsNode(node); });
          if (inside.length === 0) { inside = Array.prototype.slice.call(quote.childNodes); }
          var after = quote.cloneNode(false);
          while (inside[inside.length - 1].nextSibling) { after.appendChild(inside[inside.length - 1].nextSibling); }
          var parent = quote.parentNode;
          var next = quote.nextSibling;
          inside.forEach(function (node) { parent.insertBefore(node, next); });
          if (after.firstChild) { parent.insertBefore(after, next); }
          if (!quote.firstChild) { parent.removeChild(quote); }
          last = inside[inside.length - 1];
        } else {
          var blocks = Array.prototype.filter.call(editable.childNodes, function (node) { return range.intersectsNode(node); });
          if (blocks.length === 0) {
            var line = document.createElement('div');
            line.appendChild(document.createElement('br'));
            editable.appendChild(line);
            blocks = [line];
          }
          var wrapper = document.createElement('blockquote');
          wrapper.className = 'af-quote';
          editable.insertBefore(wrapper, blocks[0]);
          blocks.forEach(function (node) { wrapper.appendChild(node); });
          last = wrapper;
        }

        var restored = document.createRange();
        if (bounds[0] !== editable && bounds[2] !== editable && editable.contains(bounds[0]) && editable.contains(bounds[2])) {
          restored.setStart(bounds[0], bounds[1]);
          restored.setEnd(bounds[2], bounds[3]);
        } else {
          restored.selectNodeContents(last);
          restored.collapse(false);
        }
        select(restored);
        this.notify();
      },

      openLink() {
        var self = this;
        var editable = this.$refs.editable;
        var range = this.restore();
        if (!range) {
          this.focus();
          range = rangeIn(editable);
        }
        linkRange = range ? range.cloneRange() : null;

        var anchor = range ? closestIn(range.startContainer, 'A', editable) : null;
        this.linkEditing = anchor !== null;
        this.linkUrl = anchor ? anchor.getAttribute('href') || '' : '';
        this.linkOpen = true;
        this.$nextTick(function () {
          self.placeLink();
          if (self.$refs.linkInput) {
            self.$refs.linkInput.focus();
            self.$refs.linkInput.select();
          }
        });
      },

      // The link box opens under the selection, inside the editor's width.
      placeLink() {
        var popover = this.$refs.linkPopover;
        if (!popover) { return; }
        var parent = popover.offsetParent || this.$el;
        var area = parent.getBoundingClientRect();
        var rect = linkRange ? linkRange.getBoundingClientRect() : null;
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          var holder = linkRange ? linkRange.startContainer : this.$refs.editable;
          rect = (holder.nodeType === 3 ? holder.parentNode : holder).getBoundingClientRect();
        }
        var left = Math.max(0, Math.min(rect.left - area.left, parent.clientWidth - popover.offsetWidth));
        popover.style.top = (rect.bottom - area.top + parent.scrollTop + 6) + 'px';
        popover.style.left = left + 'px';
      },

      linkTarget() {
        var editable = this.$refs.editable;
        editable.focus({ preventScroll: true });
        if (!linkRange || !editable.contains(linkRange.startContainer)) { return rangeIn(editable); }
        var anchor = linkRange.collapsed ? closestIn(linkRange.startContainer, 'A', editable) : null;
        if (anchor) {
          var whole = document.createRange();
          whole.selectNodeContents(anchor);
          select(whole);

          return whole;
        }
        select(linkRange);

        return linkRange;
      },

      applyLink() {
        var url = linkAddress(this.linkUrl);
        this.linkOpen = false;
        if (url === null) {
          if (this.linkEditing) { this.removeLink(); } else { this.linkTarget(); }
          return;
        }

        var range = this.linkTarget();
        if (!range) { return; }
        if (range.collapsed) {
          var anchor = document.createElement('a');
          anchor.setAttribute('href', url);
          anchor.textContent = url;
          document.execCommand('insertHTML', false, anchor.outerHTML);
        } else {
          document.execCommand('createLink', false, url);
        }
        this.changed();
      },

      removeLink() {
        this.linkOpen = false;
        if (!this.linkTarget()) { return; }
        document.execCommand('unlink', false, null);
        this.changed();
      },

      cancelLink() {
        if (!this.linkOpen) { return; }
        this.linkOpen = false;
        var editable = this.$refs.editable;
        editable.focus({ preventScroll: true });
        if (linkRange && editable.contains(linkRange.startContainer)) { select(linkRange); }
      },

      // A click anywhere else closes the link box; the Link button's own click opens it.
      closeLink(event) {
        var target = event && event.target;
        if (target && target.closest && target.closest('[data-af-editor-command="link"]')) { return; }
        this.linkOpen = false;
      },

      paste(event) {
        var data = event.clipboardData;
        if (!data) { return; }
        if (Array.prototype.indexOf.call(data.types || [], 'text/html') !== -1) { return; }

        var files = Array.prototype.filter.call(data.files || [], isPicture);
        if (files.length > 0) {
          event.preventDefault();
          files.forEach(this.uploadPicture.bind(this));
          return;
        }

        var text = data.getData('text/plain');
        if (!text) { return; }
        event.preventDefault();
        if (/[\r\n]/.test(text)) {
          document.execCommand('insertHTML', false, linesAsHtml(text));
        } else {
          document.execCommand('insertText', false, text);
        }
        this.changed();
      },

      drop(event) {
        var files = event.dataTransfer ? Array.prototype.slice.call(event.dataTransfer.files || []) : [];
        if (files.length === 0) { return; }
        event.preventDefault();

        var range = caretRangeAt(event.clientX, event.clientY);
        if (range && this.$refs.editable.contains(range.startContainer)) { savedRange = range; }
        files.filter(isPicture).forEach(this.uploadPicture.bind(this));
      },

      pickPictures(event) {
        Array.prototype.slice.call(event.target.files || []).filter(isPicture).forEach(this.uploadPicture.bind(this));
        event.target.value = '';
      },

      // A picture goes up through the component's own upload, and is shown from the address it answers.
      uploadPicture(file) {
        var self = this;
        var pictures = config.pictures;
        if (!pictures) { return; }
        if (file.size > pictures.maxBytes) {
          this.$dispatch('af-editor-refused', file.name + ' is larger than ' + Math.round(pictures.maxBytes / 1048576) + ' MB. Attach it as a file instead.');
          return;
        }

        var at = savedRange ? savedRange.cloneRange() : null;
        var failed = function () {
          self.$dispatch('af-editor-uploading', -1);
          self.$dispatch('af-editor-refused', 'That picture could not be added.');
        };

        this.$dispatch('af-editor-uploading', 1);
        this.$wire.upload(pictures.property, file, function (uploadName) {
          self.$wire.call(pictures.url, uploadName).then(function (url) {
            if (!url) { failed(); return; }
            self.$dispatch('af-editor-uploading', -1);
            self.insertPicture(url, at);
          }, failed);
        }, failed);
      },

      insertPicture(url, at) {
        var editable = this.$refs.editable;
        if (!editable || !editable.isConnected) { return; }
        editable.focus({ preventScroll: true });
        if (at && editable.contains(at.startContainer)) {
          select(at);
        } else if (!rangeIn(editable)) {
          var end = document.createRange();
          end.selectNodeContents(editable);
          end.collapse(false);
          select(end);
        }
        document.execCommand('insertImage', false, url);
        this.changed();
      },
    };
  }

  function register() {
    window.Alpine.data('afEditor', afEditor);
  }

  if (window.Alpine) { register(); } else { document.addEventListener('alpine:init', register); }
})();
